import { TZDate } from "@date-fns/tz";
import { useI18nContext } from "@root/utils/organisation";
import { type MouseEvent, useMemo } from "react";
import { CalendarCell } from "./cell";
import { getCalendarCells } from "./logics";
import { CalendarContainer, classes } from "./styles";
import { Day, type TCalendarEvent, type TCalendarEventRange } from "./types";

export interface ICalendarProps<T> {
  date: number;
  events: TCalendarEvent<T>[];
  onCellClick?: (date: Date) => void;
  onEventClick?: (
    htmlEvent: MouseEvent<HTMLDivElement>,
    event: TCalendarEventRange<T>,
    events: TCalendarEventRange<T>[]
  ) => void;
  onViewMoreClick?: (events: TCalendarEventRange<T>[]) => void;
  skipWeekend?: boolean;
}

export const Calendar = <T,>({
  date,
  events,
  onCellClick,
  onEventClick,
  onViewMoreClick,
  skipWeekend = true,
}: ICalendarProps<T>) => {
  const { timeZone } = useI18nContext();
  const cellPerLine = skipWeekend ? 5 : 7;
  const cells = useMemo(
    () => getCalendarCells(events, new TZDate(date, timeZone), cellPerLine, timeZone, Day.MONDAY, skipWeekend),
    [events, date, cellPerLine, skipWeekend, timeZone]
  );

  const lineCount = cells.length / cellPerLine;

  return (
    <CalendarContainer className={classes.root} cellPerLine={cellPerLine} lineCount={lineCount}>
      {cells.map((cell, index) => (
        <CalendarCell
          key={cell.date.getTime()}
          index={index}
          cellPerLine={cellPerLine}
          cell={cell}
          timeZone={timeZone}
          onCellClick={onCellClick}
          onEventClick={onEventClick}
          onViewMoreClick={onViewMoreClick}
        />
      ))}
    </CalendarContainer>
  );
};

export * from "./types";
export type * from "./types/timeZone";
